/** Janela "Sobre": o que é o portal, como usar, tipos de imagem e fontes. */
import { ExternalLink, RotateCcw } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { CATEGORY_LABELS, type ProductCategory } from '../config/products';
import { EMAIL_CONTATO } from '../termos/contato';
import { STAR_SECTOR_PAGE } from '../goes/star';
import { PROVEDOR_FUNDO } from '../map/fundoCreditos';
import { useMapInstance } from '../map/mapContext';
import { isGeorefCalibrated } from '../map/projection';
import { useUiStore } from '../state/ui';
import { resetPreferences } from '../state/urlSync';
import { Brand } from './Brand';
import { CATEGORY_HELP } from './ProductPicker';
import { creditosNoMapa } from './creditos';
import { Button, Dialog } from './primitives';

const SOURCES: { name: string; what: string; url: string }[] = [
  {
    name: 'pyForTraCC',
    what: 'Rastreio e previsão de nuvens: os sistemas de nuvens frias do canal 13 do GOES-19, a cada 10 min',
    url: 'https://github.com/fortracc/pyfortracc',
  },
  {
    name: 'pyForTraCC · GSMaP (JAXA)',
    what: 'Rastreio da chuva: as áreas com chuva de pelo menos 0,1 mm/h do GSMaP_NOW, a cada 30 min',
    url: 'https://github.com/fortracc/pyfortracc',
  },
  ...(PROVEDOR_FUNDO.id === 'esri'
    ? [
        {
          name: 'Esri World Imagery (Powered by Esri)',
          what: 'Foto de satélite do fundo, depois do zoom máximo do GOES: um mosaico de fotos de datas variadas, que pode ter nuvens e não mostra fumaça nem fogo de agora. Fontes: Esri, Vantor, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN e a GIS User Community',
          url: 'https://www.arcgis.com/home/item.html?id=10df2279f9684e4a9f6a7f08febac2a9',
        },
        {
          name: 'Esri — rótulos sobre a foto',
          what: 'Ruas e nomes sobre a foto do fundo. Fontes: Esri, TomTom, Garmin, FAO, NOAA, USGS, © colaboradores do OpenStreetMap e a GIS User Community',
          url: 'https://www.esri.com',
        },
      ]
    : [
        {
          name: 'EOxCloudless (EOX IT Services GmbH)',
          what: 'Foto de satélite do fundo, depois do zoom máximo do GOES: mosaico sem nuvens do Sentinel-2 de 2025 (10 m). Contém dados modificados do Copernicus Sentinel (no original: “Contains modified Copernicus Sentinel data 2025”), licença CC BY-NC-SA 4.0',
          url: 'https://cloudless.eox.at',
        },
        {
          name: 'OpenStreetMap · EOX::Maps',
          what: 'Ruas e nomes sobre a foto do fundo: © colaboradores do OpenStreetMap; desenho © EOX',
          url: 'https://www.openstreetmap.org/copyright',
        },
      ]),
  { name: 'NOAA/NESDIS/STAR', what: 'Imagens do satélite GOES-19 (setor Norte da América do Sul)', url: STAR_SECTOR_PAGE },
  {
    name: 'NOAA/STAR · CIRA · NASA SPoRT',
    what: 'Legenda das cores: escalas oficiais do STAR e guias rápidos de interpretação das imagens',
    url: 'https://rammb.cira.colostate.edu/training/visit/quick_guides/',
  },
  {
    name: 'SIPAM/Censipam',
    what: 'Radares meteorológicos da Amazônia (refletividade e chuva estimada), espelhados a cada 15 min',
    url: 'https://www.gov.br/censipam/pt-br',
  },
  {
    name: 'NOAA — GOES-19 ABI (ADP)',
    what: 'Fumaça: a máscara do produto de detecção de aerossóis, em contorno, a cada 10 min',
    url: 'https://noaa-goes19.s3.amazonaws.com/index.html#ABI-L2-ADPF/',
  },
  {
    name: 'Rede de Qualidade do Ar do Acre (UFAC/MPAC)',
    what: 'Qualidade do ar: MP2,5 dos sensores PurpleAir da rede, com a correção da própria rede (LRAPA), espelhado a cada 5 min',
    url: 'https://acrequalidadedoar.ufac.br/',
  },
  {
    name: 'RedeAr (IPAM/UFPA)',
    what: 'Qualidade do ar: sensores de baixo custo das secretarias de meio ambiente, universidades e do IPAM na Amazônia Legal (bruto dos canais A e B, corrigido pelo AmaView com a mesma fórmula da rede do Acre)',
    url: 'https://redear.org.br/',
  },
  {
    name: 'PurpleAir',
    what: 'Fabricante da maior parte dos sensores de baixo custo da camada de qualidade do ar. Data from PurpleAir',
    url: 'https://www2.purpleair.com/',
  },
  {
    name: 'AirGradient',
    what: 'Qualidade do ar: sensores públicos do mapa da AirGradient na Amazônia Legal',
    url: 'https://www.airgradient.com/map/',
  },
  {
    name: 'OpenAQ',
    what: 'Qualidade do ar: sensores repassados pela plataforma aberta da OpenAQ, quando o espelho tem a chave',
    url: 'https://explore.openaq.org/',
  },
  {
    name: 'MonitorAr — MMA',
    what: 'Qualidade do ar: IQAr e classificação oficiais (CONAMA 491) das estações dos órgãos estaduais',
    url: 'https://monitorar.mma.gov.br/',
  },
  {
    name: 'CAMS — Copernicus/ECMWF, via Open-Meteo',
    what: 'Qualidade do ar: estimativa do modelo global (não é medição). Contains modified Copernicus Atmosphere Monitoring Service information',
    url: 'https://open-meteo.com/en/docs/air-quality-api',
  },
  { name: 'INPE — Programa Queimadas', what: 'Focos de queimada detectados por satélite', url: 'https://terrabrasilis.dpi.inpe.br/queimadas/portal/' },
  {
    name: 'MapBiomas — Coleção 11',
    what: 'Ficha do foco: histórico anual (1985–2025) do uso e cobertura da terra no raio de 375 m em volta do foco',
    url: 'https://brasil.mapbiomas.org/',
  },
  {
    name: 'SICAR/SFB — Cadastro Ambiental Rural',
    what: 'Ficha do foco: imóveis declarados no CAR que contêm o ponto (só os dados públicos: código, situação, área e tipo)',
    url: 'https://www.car.gov.br/',
  },
  {
    name: 'CEMADEN/MCTI',
    what: 'Chuva medida pelos pluviômetros automáticos da Rede Observacional',
    url: 'https://www.gov.br/cemaden/pt-br',
  },
  {
    name: 'INMET (WIS2/OMM)',
    what: 'Estações meteorológicas automáticas e convencionais: temperatura, umidade, vento, pressão e chuva de hora em hora',
    url: 'https://portal.inmet.gov.br/',
  },
  {
    name: 'NOAA Aviation Weather Center',
    what: 'METAR dos aeródromos: vento, visibilidade e tempo presente (fumaça, névoa seca, trovoada)',
    url: 'https://aviationweather.gov/',
  },
  {
    name: 'University of Wyoming',
    what: 'Sondagens de radiossonda (perfis verticais da atmosfera)',
    url: 'https://weather.uwyo.edu/upperair/',
  },
  {
    name: 'IBGE',
    what: 'Limites de municípios e estados, sedes municipais, Amazônia Legal',
    url: 'https://www.ibge.gov.br/geociencias/organizacao-do-territorio/malhas-territoriais.html',
  },
  { name: 'FUNAI', what: 'Terras indígenas', url: 'https://www.gov.br/funai/pt-br/atuacao/terras-indigenas/geoprocessamento-e-mapas' },
  { name: 'MMA', what: 'Unidades de conservação (CNUC)', url: 'https://www.gov.br/mma/pt-br/assuntos/biodiversidade-e-biomas/areas-protegidas' },
  { name: 'DNIT', what: 'Rodovias federais', url: 'https://www.gov.br/dnit/pt-br/assuntos/atlas-e-mapas' },
];

function H(props: { children: ReactNode }) {
  return <h3 className="mt-5 mb-2 text-sm font-semibold text-white first:mt-0">{props.children}</h3>;
}

/**
 * Créditos das camadas que estão no mapa agora (ficavam no botão "i" do canto
 * do mapa): os textos das próprias fontes, inclusive os que as licenças pedem
 * junto do dado.
 */
function CreditosNoMapa(props: { open: boolean }) {
  const map = useMapInstance((s) => s.map);
  const [creditos, setCreditos] = useState<string[]>([]);
  useEffect(() => {
    if (!props.open || !map) return;
    let vivo = true;
    void creditosNoMapa(map).then((c) => {
      if (vivo) setCreditos(c);
    });
    return () => {
      vivo = false;
    };
  }, [props.open, map]);
  if (!creditos.length) return null;
  return (
    <>
      <H>Créditos dos dados no mapa</H>
      <ul className="space-y-1 text-xs text-ink-200" data-testid="creditos-no-mapa">
        {creditos.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
    </>
  );
}

export function AboutDialog() {
  const open = useUiStore((s) => s.aboutOpen);
  const set = useUiStore((s) => s.set);
  const close = () => set({ aboutOpen: false });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Sobre o AmaView"
      widthClass="max-w-2xl"
      footer={
        <Button variant="primary" onClick={close}>
          Entendi
        </Button>
      }
    >
      <div className="mb-4">
        <Brand />
      </div>
      <p className="leading-relaxed">
        O <strong className="text-white">AmaView</strong> é um projeto de pesquisa acadêmica, sem fins comerciais, que reúne, em um único portal, dados
        públicos sobre a Amazônia Legal disponibilizados por instituições de pesquisa e órgãos governamentais, nacionais e
        internacionais: imagens de satélite, radares meteorológicos, estações e pluviômetros, focos de queimada, fumaça,
        qualidade do ar e previsão do tempo.
      </p>
      <p className="mt-2 leading-relaxed">
        O objetivo é facilitar o acesso integrado a essas informações, quase em tempo real, para pesquisadores, gestores
        públicos, imprensa e qualquer cidadão. O AmaView não tem vínculo com as instituições citadas e não produz dados
        oficiais: a autoria e os créditos de cada informação pertencem às fontes listadas abaixo.
      </p>

      <H>Como usar</H>
      <ol className="list-decimal space-y-1.5 pl-5 leading-relaxed">
        <li>
          Na aba <strong className="text-white">Satélite</strong>, escolha a imagem — por exemplo “Cor natural” de dia ou
          “Infravermelho” à noite. Dá para empilhar até 3 e ajustar a opacidade de cada uma.
        </li>
        <li>
          O portal abre parado na imagem mais recente. Aperte <strong className="text-white">▶</strong> na barra de tempo
          para baixar as imagens do período e ver a animação. Escolha o período (últimas horas ou datas), o intervalo entre
          quadros e a velocidade. Atalhos: espaço, ← →, Home e End.
        </li>
        <li>
          Os focos de queimada do INPE já aparecem no mapa — na aba <strong className="text-white">Queimadas</strong> você
          escolhe quais mostrar. Na aba <strong className="text-white">Mapa</strong>, ligue ou desligue limites (municípios,
          terras indígenas, rios…). Clique no mapa para ver detalhes.
        </li>
        <li>
          Use <strong className="text-white">Compartilhar</strong> para enviar o link exatamente como está na sua tela, ou{' '}
          <strong className="text-white">Exportar</strong> para salvar um GIF ou vídeo.
        </li>
      </ol>

      <H>Tipos de imagem</H>
      <dl className="space-y-2">
        {(Object.keys(CATEGORY_LABELS) as ProductCategory[]).map((c) => (
          <div key={c}>
            <dt className="font-medium text-ink-100">{CATEGORY_LABELS[c]}</dt>
            <dd className="text-ink-300">{CATEGORY_HELP[c]}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-xs text-ink-300">
        Horários em Brasília (UTC−3) e em UTC, o horário universal usado pelos satélites. As fronteiras, a logomarca da NOAA
        e a legenda no rodapé já vêm gravadas na imagem pela própria NOAA.
      </p>

      <CreditosNoMapa open={open} />

      <H>De onde vêm os dados</H>
      <ul className="space-y-1.5">
        {SOURCES.map((s) => (
          <li key={s.name}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-mata-300 hover:text-mata-400 hover:underline"
            >
              {s.name}
              <ExternalLink className="size-3" aria-hidden />
            </a>
            <span className="text-ink-300"> — {s.what}</span>
          </li>
        ))}
      </ul>

      <H>Suas preferências</H>
      <p className="leading-relaxed">
        Camadas, período, velocidade, qualidade e a vista do mapa ficam salvos neste navegador e voltam na próxima visita
        (sempre na imagem mais recente). Um link compartilhado abre como quem o enviou deixou.
      </p>
      <div className="mt-2">
        <Button
          size="sm"
          icon={RotateCcw}
          onClick={resetPreferences}
          title="Apaga as preferências salvas neste navegador e recarrega o portal no padrão"
        >
          Restaurar padrões
        </Button>
      </div>

      <H>Aviso importante</H>
      <p className="rounded-lg border border-yellow-400/30 bg-yellow-400/10 p-3 text-xs leading-relaxed text-yellow-100">
        Os dados são produzidos e publicados pelas instituições de origem. O AmaView <strong>apenas os reúne e apresenta</strong>;
        quando há algum tratamento (recorte, conversão para o mapa ou correção de sensores), ele está descrito junto da
        fonte. Podem ocorrer atrasos, falhas ou lacunas quando as fontes estão fora do ar. Para uso oficial, consulte sempre
        as fontes.
        {!isGeorefCalibrated && ' O encaixe dos limites sobre a imagem ainda está sendo ajustado.'}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-ink-300">
        Não use o AmaView para decisões de segurança: em emergência, ligue para a Defesa Civil (199) ou os Bombeiros (193).
      </p>
      <p className="mt-4 text-xs text-ink-300">
        <a
          href="/termos"
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1 font-medium text-mata-300 hover:text-mata-400 hover:underline"
          data-testid="link-termos"
        >
          Termos de uso e privacidade
          <ExternalLink className="size-3" aria-hidden />
        </a>
        <span className="text-ink-400"> — sem cadastro e sem cookies de rastreamento; as visitas são contadas só em totais anônimos, e suas preferências ficam no seu aparelho.</span>
      </p>
      <p className="mt-2 text-xs text-ink-300" data-testid="contato-sobre">
        Encontrou algo errado ou tem uma dúvida?{' '}
        <a href={`mailto:${EMAIL_CONTATO}`} className="font-medium text-mata-300 hover:text-mata-400 hover:underline">
          {EMAIL_CONTATO}
        </a>
      </p>
    </Dialog>
  );
}
